import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  IconButton,
  Collapse,
  LinearProgress
} from '@mui/material';
import {
  BugReport,
  Speed,
  Security,
  Code,
  PlayArrow,
  ExpandMore,
  ExpandLess,
  CheckCircle,
  Error,
  Warning,
  Info
} from '@mui/icons-material';
import axios from 'axios';

interface AgentCapability {
  name: string;
  description: string;
  features: string[];
}

interface TestResult {
  name: string;
  type: string;
  status: string;
  duration: number;
  message?: string;
  metric?: string;
  value?: number;
  threshold?: number;
  unit?: string;
}

interface DebugFinding {
  type: string;
  pattern: string;
  count: number;
  severity: string;
  category?: string;
}

interface FixAction {
  type: string;
  description: string;
  status: string;
  [key: string]: any;
}

const SystemAgents = () => {
  const [capabilities, setCapabilities] = useState<{
    debug: AgentCapability;
    qa: AgentCapability;
    engineer: AgentCapability;
  } | null>(null);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [debugTarget, setDebugTarget] = useState('');
  const [testSuite, setTestSuite] = useState('smoke');
  const [issueType, setIssueType] = useState('bug');
  const [issueDescription, setIssueDescription] = useState('');
  
  const [debugResults, setDebugResults] = useState<any>(null);
  const [qaResults, setQaResults] = useState<any>(null);
  const [engineerResults, setEngineerResults] = useState<any>(null);
  
  const [expandedSections, setExpandedSections] = useState<{[key: string]: boolean}>({
    debug: true,
    qa: true,
    engineer: true
  });

  useEffect(() => {
    fetchCapabilities();
  }, []);

  const fetchCapabilities = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/system-agents/capabilities', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCapabilities(response.data.data);
    } catch (err: any) {
      console.error('Failed to fetch capabilities:', err);
    }
  };

  const runDebugAnalysis = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/system-agents/debug/analyze', {
        targetPath: debugTarget || 'src/server.js',
        options: {
          checkErrors: true,
          checkPerformance: true,
          checkSecurity: true
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDebugResults(response.data.data);
      setSuccess('Debug analysis completed successfully');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to run debug analysis');
    } finally {
      setLoading(false);
    }
  };

  const runQATests = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/system-agents/qa/test', {
        testSuite,
        options: {}
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setQaResults(response.data.data);
      setSuccess('QA tests completed successfully');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to run QA tests');
    } finally {
      setLoading(false);
    }
  };

  const runEngineerFix = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/system-agents/engineer/fix', {
        issue: {
          type: issueType,
          description: issueDescription,
          file: debugTarget
        },
        options: {}
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEngineerResults(response.data.data);
      setSuccess('Engineer fix completed successfully');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to run engineer fix');
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical': return <Error color="error" />;
      case 'high': return <Error color="error" />;
      case 'medium': return <Warning color="warning" />;
      case 'low': return <Info color="info" />;
      default: return <Info />;
    }
  };

  const getTestStatusIcon = (status: string) => {
    switch (status) {
      case 'passed': return <CheckCircle color="success" />;
      case 'failed': return <Error color="error" />;
      case 'skipped': return <Warning color="warning" />;
      default: return <Info />;
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        System Agents Control Panel
      </Typography>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <BugReport sx={{ mr: 1 }} />
                <Typography variant="h6">Debug Agent</Typography>
                <IconButton 
                  size="small" 
                  onClick={() => toggleSection('debug')}
                  sx={{ ml: 'auto' }}
                >
                  {expandedSections.debug ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              
              <Collapse in={expandedSections.debug}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {capabilities?.debug?.description}
                </Typography>
                
                <TextField
                  fullWidth
                  label="Target Path"
                  value={debugTarget}
                  onChange={(e) => setDebugTarget(e.target.value)}
                  placeholder="e.g., src/server.js"
                  margin="normal"
                  size="small"
                />
                
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={runDebugAnalysis}
                  disabled={loading}
                  startIcon={<PlayArrow />}
                  sx={{ mt: 2 }}
                >
                  Run Analysis
                </Button>
                
                {debugResults && (
                  <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      Findings: {debugResults.findings?.length || 0}
                    </Typography>
                    {debugResults.findings?.slice(0, 3).map((finding: DebugFinding, index: number) => (
                      <Box key={index} display="flex" alignItems="center" mt={1}>
                        {getSeverityIcon(finding.severity)}
                        <Typography variant="caption" sx={{ ml: 1 }}>
                          {finding.type} ({finding.count})
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Collapse>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Speed sx={{ mr: 1 }} />
                <Typography variant="h6">QA Agent</Typography>
                <IconButton 
                  size="small" 
                  onClick={() => toggleSection('qa')}
                  sx={{ ml: 'auto' }}
                >
                  {expandedSections.qa ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              
              <Collapse in={expandedSections.qa}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {capabilities?.qa?.description}
                </Typography>
                
                <FormControl fullWidth margin="normal" size="small">
                  <InputLabel>Test Suite</InputLabel>
                  <Select
                    value={testSuite}
                    onChange={(e) => setTestSuite(e.target.value)}
                    label="Test Suite"
                  >
                    <MenuItem value="smoke">Smoke Tests</MenuItem>
                    <MenuItem value="api">API Tests</MenuItem>
                    <MenuItem value="security">Security Tests</MenuItem>
                    <MenuItem value="performance">Performance Tests</MenuItem>
                    <MenuItem value="all">All Tests</MenuItem>
                  </Select>
                </FormControl>
                
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={runQATests}
                  disabled={loading}
                  startIcon={<PlayArrow />}
                  sx={{ mt: 2 }}
                >
                  Run Tests
                </Button>
                
                {qaResults && qaResults.summary && (
                  <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      Test Results
                    </Typography>
                    <Box display="flex" justifyContent="space-between" mt={1}>
                      <Chip 
                        label={`Passed: ${qaResults.summary.passed}`} 
                        color="success" 
                        size="small" 
                      />
                      <Chip 
                        label={`Failed: ${qaResults.summary.failed}`} 
                        color="error" 
                        size="small" 
                      />
                      <Chip 
                        label={`Total: ${qaResults.summary.total}`} 
                        size="small" 
                      />
                    </Box>
                    {qaResults.summary.total > 0 && (
                      <LinearProgress 
                        variant="determinate" 
                        value={(qaResults.summary.passed / qaResults.summary.total) * 100}
                        sx={{ mt: 1 }}
                      />
                    )}
                  </Box>
                )}
              </Collapse>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Code sx={{ mr: 1 }} />
                <Typography variant="h6">Engineer Agent</Typography>
                <IconButton 
                  size="small" 
                  onClick={() => toggleSection('engineer')}
                  sx={{ ml: 'auto' }}
                >
                  {expandedSections.engineer ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              
              <Collapse in={expandedSections.engineer}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {capabilities?.engineer?.description}
                </Typography>
                
                <FormControl fullWidth margin="normal" size="small">
                  <InputLabel>Issue Type</InputLabel>
                  <Select
                    value={issueType}
                    onChange={(e) => setIssueType(e.target.value)}
                    label="Issue Type"
                  >
                    <MenuItem value="bug">Bug Fix</MenuItem>
                    <MenuItem value="performance">Performance</MenuItem>
                    <MenuItem value="security">Security</MenuItem>
                    <MenuItem value="refactor">Refactor</MenuItem>
                  </Select>
                </FormControl>
                
                <TextField
                  fullWidth
                  label="Issue Description"
                  value={issueDescription}
                  onChange={(e) => setIssueDescription(e.target.value)}
                  placeholder="Describe the issue..."
                  margin="normal"
                  size="small"
                  multiline
                  rows={2}
                />
                
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={runEngineerFix}
                  disabled={loading || !issueDescription}
                  startIcon={<PlayArrow />}
                  sx={{ mt: 2 }}
                >
                  Run Fix
                </Button>
                
                {engineerResults && (
                  <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      Status: {engineerResults.status}
                    </Typography>
                    {engineerResults.actions?.slice(0, 3).map((action: FixAction, index: number) => (
                      <Box key={index} display="flex" alignItems="center" mt={1}>
                        <CheckCircle color="success" sx={{ fontSize: 16, mr: 1 }} />
                        <Typography variant="caption">
                          {action.description}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Collapse>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {(debugResults || qaResults || engineerResults) && (
        <Paper sx={{ mt: 3, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Detailed Results
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {debugResults && debugResults.findings && (
            <Box mb={3}>
              <Typography variant="subtitle1" gutterBottom>
                Debug Analysis Results
              </Typography>
              <List dense>
                {debugResults.findings.map((finding: DebugFinding, index: number) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center">
                          {getSeverityIcon(finding.severity)}
                          <Typography variant="body2" sx={{ ml: 1 }}>
                            {finding.type}
                          </Typography>
                          <Chip
                            label={finding.severity}
                            color={getSeverityColor(finding.severity) as any}
                            size="small"
                            sx={{ ml: 'auto' }}
                          />
                        </Box>
                      }
                      secondary={`Found ${finding.count} occurrence(s)`}
                    />
                  </ListItem>
                ))}
              </List>
              
              {debugResults.recommendations && debugResults.recommendations.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    Recommendations
                  </Typography>
                  <List dense>
                    {debugResults.recommendations.map((rec: any, index: number) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={rec.action}
                          secondary={rec.details}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </Box>
          )}
          
          {qaResults && qaResults.tests && (
            <Box mb={3}>
              <Typography variant="subtitle1" gutterBottom>
                QA Test Results
              </Typography>
              <List dense>
                {qaResults.tests.map((test: TestResult, index: number) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center">
                          {getTestStatusIcon(test.status)}
                          <Typography variant="body2" sx={{ ml: 1 }}>
                            {test.name}
                          </Typography>
                          <Typography variant="caption" sx={{ ml: 'auto' }}>
                            {test.duration}ms
                          </Typography>
                        </Box>
                      }
                      secondary={test.message || `Type: ${test.type}`}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          
          {engineerResults && engineerResults.actions && (
            <Box mb={3}>
              <Typography variant="subtitle1" gutterBottom>
                Engineer Fix Actions
              </Typography>
              <List dense>
                {engineerResults.actions.map((action: FixAction, index: number) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center">
                          <CheckCircle color="success" sx={{ fontSize: 20, mr: 1 }} />
                          <Typography variant="body2">
                            {action.type.toUpperCase()}: {action.description}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        action.changes ? `${action.changes.length} changes made` :
                        action.optimizations ? action.optimizations.join(', ') :
                        action.steps ? action.steps.join(' → ') : null
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default SystemAgents;